import React, { useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Stack, Chip, Button,
  TextField, MenuItem, FormControl, InputLabel, Select, InputAdornment,
  CircularProgress, LinearProgress, alpha, Paper, Avatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  useCycleAttendance,
  useDownloadMonthlyAttendanceExport,
  useDownloadAttendanceExport,
} from '../../hooks/useAttendance';
import { getCurrentCycle, getCycleByParam } from '../../utils/attendanceCycle';
import CycleSelector from './CycleSelector';
import EmployeeAttendanceCalendar from './EmployeeAttendanceCalendar';
import { SELECTED_MENU_PROPS } from '../StyledComponents/StyledComponent';

// ---- FORTHEYE soft-card tokens (shared with the leave / payslip dashboards) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff'; // primary blue

// Selects / inputs share a soft rounded look inside the filter card.
const SOFT_INPUT_SX = { '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } };
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px', py: 1,
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

const CycleAttendanceDashboard = ({ teamOnly = false }) => {
  const [cycle, setCycle] = useState(getCurrentCycle().cycle);
  const [department, setDepartment] = useState('');
  const [teamHead, setTeamHead] = useState('all');
  const [manager, setManager] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [reportMode, setReportMode] = useState('summary'); // 'summary' | 'daily'
  const [calendarEmp, setCalendarEmp] = useState(null);

  const params = useMemo(
    () => ({
      cycle,
      department: department || undefined,
      team_head: teamHead !== 'all' ? teamHead : undefined,
      manager: manager !== 'all' ? manager : undefined,
      search: search || undefined,
      team_scope: teamOnly ? 1 : undefined,
    }),
    [cycle, department, teamHead, manager, search, teamOnly],
  );

  const { data, isLoading, isFetching } = useCycleAttendance(params);
  const { mutate: downloadReport, isPending: isDownloadingSummary } = useDownloadMonthlyAttendanceExport();
  const { mutate: downloadDaily, isPending: isDownloadingDaily } = useDownloadAttendanceExport();
  const isDownloading = isDownloadingSummary || isDownloadingDaily;

  const teamHeads = data?.team_heads || [];
  const managers = data?.managers || [];

  // Every download is scoped to the selected 26→25 cycle (e.g. 26 May – 25 Jun),
  // never a calendar month or the whole roster's history.
  const handleDownload = () => {
    const cyc = getCycleByParam(cycle);
    if (reportMode === 'daily') {
      // Day-wise sheet over the exact cycle date range.
      downloadDaily({
        startDate: cyc.startISO,
        endDate: cyc.endISO,
        department: department || undefined,
        teamHead, manager, search: search || undefined,
      });
      return;
    }
    // One-row-per-employee summary; `cycle` alone makes the backend export the
    // exact 26→25 range, so no month/year fallback is needed here.
    downloadReport({
      cycle,
      department: department || undefined, teamHead, manager, search: search || undefined,
    });
  };

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const copy = [...data.rows];
    if (sortBy === 'attendance') copy.sort((a, b) => b.attendance_pct - a.attendance_pct);
    else if (sortBy === 'absent') copy.sort((a, b) => (b.totals?.absent || 0) - (a.totals?.absent || 0));
    else if (sortBy === 'lop') copy.sort((a, b) => (b.lop_days || 0) - (a.lop_days || 0));
    else copy.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return copy;
  }, [data, sortBy]);

  const departments = useMemo(() => {
    const set = new Set();
    (data?.rows || []).forEach((r) => r.department && set.add(r.department));
    return Array.from(set).sort();
  }, [data]);

  return (
    <Box>
      {/* Filters bar */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2, md: 2.5 }, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <CycleSelector value={cycle} onChange={setCycle} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl size="small" fullWidth sx={SOFT_INPUT_SX}>
              <InputLabel>Department</InputLabel>
              <Select
                value={department}
                label="Department"
                onChange={(e) => setDepartment(e.target.value)}
                MenuProps={SELECTED_MENU_PROPS}
              >
                <MenuItem value="">All</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl size="small" fullWidth sx={SOFT_INPUT_SX}>
              <InputLabel>Team Head</InputLabel>
              <Select value={teamHead} label="Team Head" onChange={(e) => setTeamHead(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="all">All Team Heads</MenuItem>
                {teamHeads.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          {!teamOnly && <Grid item xs={6} sm={4} md={2}>
            <FormControl size="small" fullWidth sx={SOFT_INPUT_SX}>
              <InputLabel>Manager</InputLabel>
              <Select value={manager} label="Manager" onChange={(e) => setManager(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="all">All Managers</MenuItem>
                {managers.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>}
          <Grid item xs={12} sm={8} md={3}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search employee by name or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              }}
              sx={SOFT_INPUT_SX}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl size="small" fullWidth sx={SOFT_INPUT_SX}>
              <InputLabel>Sort by</InputLabel>
              <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="name">Name (A→Z)</MenuItem>
                <MenuItem value="attendance">Attendance % (high→low)</MenuItem>
                <MenuItem value="absent">Absent days (high→low)</MenuItem>
                <MenuItem value="lop">LOP days (high→low)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {!teamOnly && <Grid item xs={6} sm={4} md={2}>
            <FormControl size="small" fullWidth sx={SOFT_INPUT_SX}>
              <InputLabel>Report</InputLabel>
              <Select value={reportMode} label="Report" onChange={(e) => setReportMode(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="summary">Cycle summary</MenuItem>
                <MenuItem value="daily">Day-wise detail</MenuItem>
              </Select>
            </FormControl>
          </Grid>}
          {!teamOnly && <Grid item xs={6} sm={4} md={2}>
            <Button
              variant="contained"
              fullWidth
              disableElevation
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={isDownloading || !(data?.rows?.length)}
              sx={GRADIENT_BTN}
            >
              {isDownloading ? '…' : 'Download'}
            </Button>
          </Grid>}
        </Grid>
        {isFetching && (
          <LinearProgress sx={{ mt: 1.5, borderRadius: 1 }} />
        )}
      </Paper>

      {/* Total overview card */}
      <OverviewCard
        label={data?.label}
        payrollMonth={data?.payroll_month}
        payrollYear={data?.payroll_year}
        overview={data?.overview}
        employeeCount={sortedRows.length}
      />

      {/* Employee cards grid */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      ) : sortedRows.length === 0 ? (
        <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 6 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CalendarIcon />
          </Box>
          <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>
            No employees match your filters.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {sortedRows.map((row) => (
            <Grid item xs={12} sm={6} md={4} key={row.employee_no}>
              <EmployeeCard
                row={row}
                onOpenCalendar={() => setCalendarEmp(row.employee_no)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {calendarEmp && (
        <EmployeeAttendanceCalendar
          open
          onClose={() => setCalendarEmp(null)}
          employeeNo={calendarEmp}
          mode="cycle"
          cycle={cycle}
        />
      )}
    </Box>
  );
};

const OverviewCard = ({ label, payrollMonth, payrollYear, overview, employeeCount }) => (
  <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2}>
      <Box>
        <Typography sx={{ color: '#6b7280', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Attendance cycle
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
          {label || '—'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600, mt: 0.5 }}>
          Payroll: {payrollMonth || '—'} {payrollYear || ''} · {employeeCount} employee{employeeCount === 1 ? '' : 's'}
        </Typography>
      </Box>
      <Stack direction="row" spacing={{ xs: 2, md: 3 }} flexWrap="wrap" useFlexGap>
        <OverviewStat label="Present" value={overview?.present_total ?? 0} color="#16a34a" />
        <OverviewStat label="Absent" value={overview?.absent_total ?? 0} color="#ef4444" />
        <OverviewStat label="Leaves" value={overview?.leave_total ?? 0} color="#ef4444" />
        <OverviewStat label="Holidays" value={overview?.holiday_total ?? 0} color="#6b7280" />
        <OverviewStat label="Not recorded" value={overview?.not_recorded_total ?? 0} color="#9ca3af" />
        <OverviewStat label="LOP days" value={overview?.lop_total ?? 0} color="#ef4444" />
      </Stack>
    </Stack>
  </Paper>
);

const OverviewStat = ({ label, value, color }) => (
  <Box sx={{ minWidth: 90 }}>
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: '#111827', lineHeight: 1.1 }}>{value}</Typography>
    </Stack>
    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>{label}</Typography>
  </Box>
);

const EmployeeCard = ({ row, onOpenCalendar }) => {
  const pct = row.attendance_pct ?? 0;
  const pctColor = pct >= 90 ? '#16a34a' : pct >= 75 ? '#f59e0b' : '#ef4444';
  const initials = (row.name || '?').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
  const lop = row.lop_days ?? 0;

  return (
    <Card
      elevation={0}
      sx={{
        ...CARD_SX,
        height: '100%',
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 12px 28px rgba(17,24,39,0.08)',
        },
        cursor: 'pointer',
      }}
      onClick={onOpenCalendar}
    >
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: `${ACCENT}14`, color: ACCENT, fontWeight: 700 }}>
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ color: '#111827' }}>
              {row.name}
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }} noWrap>
              {row.employee_no} · {row.designation || '—'}
            </Typography>
          </Box>
          {lop > 0 && (
            <Chip
              size="small"
              label={`LOP ${lop}`}
              sx={{ bgcolor: '#fef2f2', color: '#ef4444', fontWeight: 700, border: 'none' }}
            />
          )}
        </Stack>

        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 2 }}>
          {row.department || '—'} · {row.reporting_manager || row.team_head || '—'}
        </Typography>

        <Box
          sx={{
            p: 2,
            borderRadius: '16px',
            bgcolor: '#f8fafc',
            border: '1px solid #eef0f4',
            mb: 2,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1.5}>
            <Typography variant="caption" sx={{ color: '#6b7280' }} fontWeight={600}>
              Attendance
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ color: pctColor }}>
              {pct}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(pctColor, 0.12),
              '& .MuiLinearProgress-bar': { bgcolor: pctColor, borderRadius: 4 },
            }}
          />
          <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
            <MiniStat label="P" value={row.totals?.present || 0} fg="#16a34a" bg="#f0fdf4" />
            <MiniStat label="A" value={row.totals?.absent || 0} fg="#ef4444" bg="#fef2f2" />
            <MiniStat label="L" value={row.totals?.leave || 0} fg={ACCENT} bg="#eef4ff" />
            <MiniStat label="H" value={row.totals?.holiday || 0} fg="#6b7280" bg="#f3f4f6" />
            <MiniStat label="—" value={row.totals?.not_recorded || 0} fg="#9ca3af" bg="#f3f4f6" />
            <MiniStat label="LOP" value={lop} fg="#ef4444" bg="#fef2f2" />
          </Stack>
        </Box>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<ViewIcon />}
          onClick={(e) => { e.stopPropagation(); onOpenCalendar(); }}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            borderColor: `${ACCENT}55`,
            color: ACCENT,
            '&:hover': { borderColor: ACCENT, bgcolor: `${ACCENT}0d` },
          }}
        >
          View Cycle Calendar
        </Button>
      </CardContent>
    </Card>
  );
};

const MiniStat = ({ label, value, fg, bg }) => (
  <Chip
    size="small"
    label={`${label} ${value}`}
    sx={{
      bgcolor: bg,
      color: fg,
      fontWeight: 700,
      fontSize: 11,
      height: 22,
      border: 'none',
    }}
  />
);

export default CycleAttendanceDashboard;
